export type Rng = {
  float: () => number;
  intBetween: (minInclusive: number, maxInclusive: number) => number;
  pick: <T>(arr: readonly T[]) => T;
  sample: <T>(arr: readonly T[], n: number) => T[];
};

export function createRng(seed: number): Rng {
  let state = seed >>> 0;
  if (state === 0) state = 0x9e3779b9;

  const float = (): number => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const intBetween = (minInclusive: number, maxInclusive: number): number => {
    if (maxInclusive < minInclusive) {
      throw new Error("intBetween: max < min");
    }
    const span = maxInclusive - minInclusive + 1;
    return minInclusive + Math.floor(float() * span);
  };

  const pick = <T,>(arr: readonly T[]): T => {
    if (arr.length === 0) throw new Error("pick: empty array");
    return arr[intBetween(0, arr.length - 1)] as T;
  };

  const sample = <T,>(arr: readonly T[], n: number): T[] => {
    if (n < 0) throw new Error("sample: negative n");
    if (n > arr.length) throw new Error("sample: n > array length");
    const pool = arr.slice();
    const out: T[] = [];
    for (let i = 0; i < n; i++) {
      const idx = intBetween(0, pool.length - 1);
      out.push(pool[idx] as T);
      pool[idx] = pool[pool.length - 1] as T;
      pool.pop();
    }
    return out;
  };

  return { float, intBetween, pick, sample };
}
